import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { useAuth } from './hooks/useAuth';
import { lazyPage } from './lib/lazyPage';
import { isGuestPath } from './lib/navigation';

/**
 * Prihlásenie a prehľad sa načítavajú hneď — sú to jediné dve obrazovky, na
 * ktorých appka začína, takže odložiť ich by znamenalo iba pridať čakanie.
 */
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';

/**
 * Všetko ostatné až vtedy, keď na to človek príde. Najviac na tom získa doska
 * s tiketmi, testy a chat: dokopy vyše sedemtisíc riadkov, ktoré pri otvorení
 * prehľadu nikto nepotrebuje.
 */
const ChangePasswordPage = lazyPage(() => import('./pages/ChangePasswordPage'), 'ChangePasswordPage');
const ChatPage = lazyPage(() => import('./pages/ChatPage'), 'ChatPage');
const ExpensesPage = lazyPage(() => import('./pages/ExpensesPage'), 'ExpensesPage');
const IdeasPage = lazyPage(() => import('./pages/IdeasPage'), 'IdeasPage');
const NotesPage = lazyPage(() => import('./pages/NotesPage'), 'NotesPage');
const InboxPage = lazyPage(() => import('./pages/InboxPage'), 'InboxPage');
const PlanPage = lazyPage(() => import('./pages/PlanPage'), 'PlanPage');
const ProjectDetailPage = lazyPage(() => import('./pages/ProjectDetailPage'), 'ProjectDetailPage');
const ProjectsListPage = lazyPage(() => import('./pages/ProjectsListPage'), 'ProjectsListPage');
const PublicRunReportPage = lazyPage(() => import('./pages/PublicRunReportPage'), 'PublicRunReportPage');
const RunPage = lazyPage(() => import('./pages/RunPage'), 'RunPage');
const AdminSettingsPage = lazyPage(() => import('./pages/settings/AdminSettingsPage'), 'AdminSettingsPage');
const AppearanceSettingsPage = lazyPage(() => import('./pages/settings/AppearanceSettingsPage'), 'AppearanceSettingsPage');
const DevicesSettingsPage = lazyPage(() => import('./pages/settings/DevicesSettingsPage'), 'DevicesSettingsPage');
const DiagnosticsSettingsPage = lazyPage(() => import('./pages/settings/DiagnosticsSettingsPage'), 'DiagnosticsSettingsPage');
const NotificationsSettingsPage = lazyPage(() => import('./pages/settings/NotificationsSettingsPage'), 'NotificationsSettingsPage');
const SettingsIndexPage = lazyPage(() => import('./pages/settings/SettingsIndexPage'), 'SettingsIndexPage');
const UpdatesSettingsPage = lazyPage(() => import('./pages/settings/UpdatesSettingsPage'), 'UpdatesSettingsPage');
const MotionPreviewPage = lazyPage(() => import('./pages/MotionPreviewPage'), 'MotionPreviewPage');
const ProductDetailPage = lazyPage(() => import('./pages/ProductDetailPage'), 'ProductDetailPage');
const ProductsPage = lazyPage(() => import('./pages/ProductsPage'), 'ProductsPage');
const TeamPage = lazyPage(() => import('./pages/TeamPage'), 'TeamPage');
const UserProfilePage = lazyPage(() => import('./pages/UserProfilePage'), 'UserProfilePage');
const DigestPage = lazyPage(() => import('./pages/DigestPage'), 'DigestPage');
const TimeExportPage = lazyPage(() => import('./pages/TimeExportPage'), 'TimeExportPage');
const GuestHomePage = lazyPage(() => import('./pages/GuestHomePage'), 'GuestHomePage');
// Verejná stránka pozvánky: bez prihlásenia a bez rámu appky, ako prihlásenie.
const InvitationPage = lazyPage(() => import('./pages/InvitationPage'), 'InvitationPage');

/**
 * Načítavanie obsahu vnútri appky. Schválne nie celostránkový spinner: rám
 * appky (navigácia, hlavička) ostáva stáť, takže prepnutie záložky nevyzerá
 * ako reštart.
 */
const PageFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-label="Načítavam">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

/**
 * /settings used to carry the screen in a query param. Those URLs are already
 * out in the world — in push payloads on people's phones — so they land on the
 * right screen instead of a dead scroll anchor.
 */
const SettingsEntry = () => {
  const [params] = useSearchParams();
  if (params.get('update') === '1') {
    return <Navigate to="/settings/aktualizacie" replace />;
  }
  if (params.get('tab') === 'administracia') {
    return <Navigate to="/settings/administracia" replace />;
  }
  return (
    <ProtectedLayout>
      <SettingsIndexPage />
    </ProtectedLayout>
  );
};

const ProtectedLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-950">
        <div className="panel px-6 py-4">Načítavam session...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Forced first-login password change: block the whole app until done.
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Hosť smie iba na obrazovky svojho projektu (lib/navigation.ts). Ostatné
  // adresy — napísané ručne, zo starej záložky, z odkazu v chate — ho vrátia
  // domov, kde ho GuestHomePage pošle do projektu. API by mu tie obrazovky aj
  // tak nevydalo; takto neuvidí ani ich prázdny rám a chyby.
  if (user.role === 'guest' && !isGuestPath(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppShell>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </AppShell>
  );
};

export const App = () => {
  const { user, mustChangePassword, loading } = useAuth();

  // Wait for the session probe before deciding any redirect (avoids a flash of
  // /login on hard refresh).
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      {/* Shared test-run report: no session, no app shell — the audience for
          this link has no account. */}
      <Route path="/r/:token" element={<PublicRunReportPage />} />
      {/* Pozvánka hosťa do projektu: bez session. Prihlásenému stránka sama
          povie, že pozvánka je pre nový účet (API by ju aj tak odmietlo). */}
      <Route path="/pozvanka/:token" element={<InvitationPage />} />

      {/* Development only: a bench for choosing the session-start moment.
          import.meta.env.DEV is a compile-time constant, so this route and the
          page behind it are dropped from the production bundle entirely. */}
      {import.meta.env.DEV ? (
        <Route path="/motion" element={<MotionPreviewPage />} />
      ) : null}
      <Route
        path="/login"
        element={
          user ? (
            <Navigate to={mustChangePassword ? '/change-password' : '/'} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/change-password"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : mustChangePassword ? (
            <ChangePasswordPage />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/"
        element={
          <ProtectedLayout>
            {user?.role === 'guest' ? <GuestHomePage /> : <DashboardPage />}
          </ProtectedLayout>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedLayout>
            <ProjectsListPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedLayout>
            <ProjectDetailPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedLayout>
            <ChatPage />
          </ProtectedLayout>
        }
      />
      <Route path="/profile" element={<Navigate to="/users/me" replace />} />
      <Route
        path="/team"
        element={
          <ProtectedLayout>
            <TeamPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/users/:userId"
        element={
          <ProtectedLayout>
            <UserProfilePage />
          </ProtectedLayout>
        }
      />
      <Route path="/activity" element={<Navigate to="/?tab=aktivita" replace />} />
      <Route
        path="/plan"
        element={
          <ProtectedLayout>
            <PlanPage />
          </ProtectedLayout>
        }
      />
      <Route path="/planner" element={<Navigate to="/plan" replace />} />
      {/* Settings is a stack of screens now. The legacy deep links keep working:
          notifications already sent to phones point at ?update=1, and
          ?tab=administracia was linkable. */}
      <Route path="/settings" element={<SettingsEntry />} />
      <Route
        path="/settings/notifikacie"
        element={
          <ProtectedLayout>
            <NotificationsSettingsPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/settings/aktualizacie"
        element={
          <ProtectedLayout>
            <UpdatesSettingsPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/settings/vzhlad"
        element={
          <ProtectedLayout>
            <AppearanceSettingsPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/settings/prihlasenia"
        element={
          <ProtectedLayout>
            <DevicesSettingsPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/settings/diagnostika"
        element={
          <ProtectedLayout>
            <DiagnosticsSettingsPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/settings/administracia"
        element={
          <ProtectedLayout>
            <AdminSettingsPage />
          </ProtectedLayout>
        }
      />
      <Route path="/stats" element={<Navigate to="/team?tab=statistiky" replace />} />
      <Route
        path="/notes"
        element={
          <ProtectedLayout>
            <NotesPage />
          </ProtectedLayout>
        }
      />
      {/* Týždenný prehľad — sem vedie pondelňajší push (`digestUrl` v API). */}
      <Route
        path="/tyzden"
        element={
          <ProtectedLayout>
            <DigestPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/export-casu"
        element={
          <ProtectedLayout>
            <TimeExportPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/schranka"
        element={
          <ProtectedLayout>
            <InboxPage />
          </ProtectedLayout>
        }
      />
      <Route path="/todo" element={<Navigate to="/plan?tab=zoznam" replace />} />
      <Route
        path="/ideas"
        element={
          <ProtectedLayout>
            <IdeasPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/produkty"
        element={
          <ProtectedLayout>
            <ProductsPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/produkty/:productId"
        element={
          <ProtectedLayout>
            <ProductDetailPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedLayout>
            <ExpensesPage />
          </ProtectedLayout>
        }
      />
      <Route
        path="/run"
        element={
          <ProtectedLayout>
            <RunPage />
          </ProtectedLayout>
        }
      />
      <Route path="/handoff" element={<Navigate to="/run" replace />} />
      <Route path="/management" element={<Navigate to="/settings" replace />} />
      <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
    </Routes>
    </Suspense>
  );
};
